import React, { useState } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
  useTheme,
  makeStyles,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import DashboardIcon from '@material-ui/icons/Dashboard';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import GroupIcon from '@material-ui/icons/Group';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import ContactPhoneIcon from '@material-ui/icons/ContactPhone';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { ROUTES } from '../constants/route.constant';
import { authService } from '../services';

const DRAWER_WIDTH = 240;

const useStyles = makeStyles((theme) => ({
  root: { display: 'flex', minHeight: '100vh' },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: '#1976d2',
  },
  menuButton: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('md')]: { display: 'none' },
  },
  drawer: {
    [theme.breakpoints.up('md')]: { width: DRAWER_WIDTH, flexShrink: 0 },
  },
  drawerPaper: {
    width: DRAWER_WIDTH,
    backgroundColor: '#1a2231',
    color: '#fff',
  },
  toolbar: {
    minHeight: 64,
  },
  content: {
    flexGrow: 1,
    minWidth: 0, // let flex child shrink so wide tables scroll instead of forcing overflow
    padding: 24,
    marginTop: 64,
    backgroundColor: '#f4f6f8',
    [theme.breakpoints.down('sm')]: { padding: 16 },
  },
  navLink: {
    color: '#cfd8dc',
    textDecoration: 'none',
    display: 'block',
  },
  navLinkActive: {
    backgroundColor: 'rgba(25, 118, 210, 0.25)',
    color: '#fff',
  },
  navIcon: {
    color: 'inherit',
    minWidth: 40,
  },
  brand: {
    fontWeight: 700,
    letterSpacing: 0.5,
    flexShrink: 0,
  },
  spacer: { flexGrow: 1 },
  userEmail: {
    marginRight: 12,
    fontSize: 14,
    opacity: 0.9,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 200,
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
}));

// `fullAccessOnly` items are visible only to full-access admins (restricted
// admins — those with a non-empty allowedProducts — don't see them).
const NAV_ITEMS = [
  { to: ROUTES.DASHBOARD, label: 'Dashboard', icon: <DashboardIcon /> },
  { to: ROUTES.NOTIFICATIONS, label: 'Send Notification', icon: <NotificationsActiveIcon />, fullAccessOnly: true },
  { to: ROUTES.WITHDRAWALS, label: 'Withdrawals', icon: <AccountBalanceWalletIcon /> },
  { to: ROUTES.MOCKED_TEACHERS, label: 'Mocked Teachers', icon: <GroupIcon /> },
  { to: ROUTES.LIVE_TEACHERS, label: 'Live Teachers', icon: <PeopleAltIcon /> },
  { to: ROUTES.TUTOR_LEADS, label: 'Tutor Leads', icon: <ContactPhoneIcon /> },
  { to: ROUTES.MARKETING, label: 'Marketing', icon: <WhatsAppIcon />, fullAccessOnly: true },
  { to: ROUTES.VERIFICATIONS, label: 'Verifications', icon: <VerifiedUserIcon /> },
];

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    authService.signOut();
    history.push(ROUTES.LOGIN);
  };

  const navItems = NAV_ITEMS.filter((item) => !item.fullAccessOnly || authService.isFullAccess);

  const drawerContent = (
    <>
      <div className={classes.toolbar} />
      <List>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            exact
            className={classes.navLink}
            activeClassName={classes.navLinkActive}
            onClick={() => setMobileOpen(false)}
          >
            <ListItem button>
              <ListItemIcon className={classes.navIcon}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItem>
          </NavLink>
        ))}
      </List>
    </>
  );

  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            aria-label="open navigation"
            className={classes.menuButton}
            onClick={() => setMobileOpen(true)}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" className={classes.brand}>
            Xarothi Admin
          </Typography>
          <span className={classes.spacer} />
          <span className={classes.userEmail}>{authService.userEmail || ''}</span>
          <IconButton color="inherit" onClick={handleLogout} title="Sign out">
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <nav className={classes.drawer}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          classes={{ paper: classes.drawerPaper }}
        >
          {drawerContent}
        </Drawer>
      </nav>

      <main className={classes.content}>{children}</main>
    </div>
  );
};

export default AdminLayout;
